import { FEATURE_AVAILABILITY_TYPE_CODE } from "@/v2-common/featureSurface/featureSurface.constants"
import { FEATURE_TYPE_CODE } from "@/v2-backoffice-client/feature/feature.constants"
import { FEATURE_GROUP_RULE_KIND } from "@/v2-backoffice-client/license/featureGroup/license.featureGroup.constants"
import {
  isCategoryRule,
  isTypeRule,
  pluralize,
  type FeatureGroupRuleLike,
} from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.compose.utils"
import type { FeatureGroupPickedLike } from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.compose.utils"
import {
  isPersonLens,
  type FeatureGroupLens,
} from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.lens.context"
import { subjectOfLens } from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.lens.utils"
import type { FeatureContentCatalogQuery } from "@/v2-backoffice-client/license/featureGroup/compose/__types__/license.featureGroup.contentCatalog.query"
import type { FeatureGroupRuleOptionsQuery } from "@/v2-backoffice-client/license/featureGroup/__types__/license.featureGroup.ruleOptions.query"

export type LicenseFeatureGroupContentItem =
  FeatureContentCatalogQuery["getFeatureContentCatalog"][number]

export type LicenseFeatureGroupRuleOption =
  FeatureGroupRuleOptionsQuery["getFeatureGroupRuleOptions"][number]

/** How a package reaches one item: by the class rule, by a category rule, or by hand. */
export type LicenseFeatureGroupContentGrant = "all" | "category" | "pick"

function splitList(value: string | null | undefined) {
  return (value ?? "").split(", ").filter(Boolean)
}

export function countriesOf(item: LicenseFeatureGroupContentItem) {
  return splitList(item.CountryCodes)
}

export function categoriesOf(item: LicenseFeatureGroupContentItem) {
  return splitList(item.CategoryCodes)
}

export function packagesOf(item: LicenseFeatureGroupContentItem) {
  return splitList(item.FeatureGroupNames)
}

/** Only a locked item depends on packages; everyone gets the rest. */
export function isLockedContent(item: LicenseFeatureGroupContentItem) {
  return (
    item.FeatureAvailabilityTypeCode ===
    FEATURE_AVAILABILITY_TYPE_CODE.lockedWithinPackage
  )
}

/** Layer categories are a way of granting map layers, and nothing else. */
export function canGrantByCategory(featureTypeCode: number) {
  return featureTypeCode === FEATURE_TYPE_CODE.mapLayer
}

export function hasTypeRule(
  rules: readonly FeatureGroupRuleLike[],
  featureTypeCode: number,
) {
  return rules.some((rule) => isTypeRule(rule, featureTypeCode))
}

export function hasCategoryRule(
  rules: readonly FeatureGroupRuleLike[],
  layerCategoryFeatureCode: string,
) {
  return rules.some(
    (rule) =>
      isCategoryRule(rule) &&
      rule.LayerCategoryFeatureCode === layerCategoryFeatureCode,
  )
}

/**
 * Why the package grants an item, or null when it does not. The order is
 * the order of reach: the class rule covers everything, a category rule its
 * layers, a pick one item.
 */
export function grantOf(
  item: LicenseFeatureGroupContentItem,
  rules: readonly FeatureGroupRuleLike[],
  isPicked: (code: string) => boolean,
): LicenseFeatureGroupContentGrant | null {
  if (hasTypeRule(rules, item.FeatureTypeCode)) return "all"
  if (categoriesOf(item).some((code) => hasCategoryRule(rules, code))) {
    return "category"
  }
  if (isPicked(item.Code)) return "pick"
  return null
}

/** The rule set with one class's "everything" rule set or cleared. */
export function rulesWithTypeRule(
  rules: readonly FeatureGroupRuleLike[],
  featureTypeCode: number,
  granted: boolean,
): FeatureGroupRuleLike[] {
  const kept = rules.filter(
    (rule) =>
      !isTypeRule(rule, featureTypeCode) &&
      // "Everything" makes the class's category rules redundant.
      !(granted && canGrantByCategory(featureTypeCode) && isCategoryRule(rule)),
  )
  if (!granted) return kept
  return [
    ...kept,
    {
      RuleKindCode: FEATURE_GROUP_RULE_KIND.featureType,
      FeatureTypeCode: featureTypeCode,
      LayerCategoryFeatureCode: null,
    },
  ]
}

/** The rule set with one layer category's rule set or cleared. */
export function rulesWithCategoryRule(
  rules: readonly FeatureGroupRuleLike[],
  layerCategoryFeatureCode: string,
  granted: boolean,
): FeatureGroupRuleLike[] {
  const kept = rules.filter(
    (rule) =>
      !(
        isCategoryRule(rule) &&
        rule.LayerCategoryFeatureCode === layerCategoryFeatureCode
      ),
  )
  if (!granted) return kept
  return [
    ...kept,
    {
      RuleKindCode: FEATURE_GROUP_RULE_KIND.layerCategory,
      FeatureTypeCode: null,
      LayerCategoryFeatureCode: layerCategoryFeatureCode,
    },
  ]
}

/**
 * One content class — base maps, map layers — as the editor shows it: the
 * class, its items, and the groups a user meets them in. Map layers group by
 * layer category (a layer in several categories appears in each; the ones
 * in none sit under "No category"); base maps group by the countries they
 * are offered in.
 */
export type LicenseFeatureGroupContentGroup = {
  /** The layer category's FeatureCode, or null for a grouping with no rule behind it. */
  categoryCode: string | null
  label: string
  items: readonly LicenseFeatureGroupContentItem[]
}

export type LicenseFeatureGroupContentClass = {
  featureTypeCode: number
  name: string
  description: string | null
  items: readonly LicenseFeatureGroupContentItem[]
  groups: readonly LicenseFeatureGroupContentGroup[]
}

const NO_CATEGORY_LABEL = "No category"
const NO_COUNTRY_LABEL = "No country"

function byLabel(a: { label: string }, b: { label: string }) {
  return a.label.localeCompare(b.label)
}

function groupLayersByCategory(
  items: readonly LicenseFeatureGroupContentItem[],
  categoryLabels: ReadonlyMap<string, string>,
): LicenseFeatureGroupContentGroup[] {
  const byCategory = new Map<string, LicenseFeatureGroupContentItem[]>()
  const unfiled: LicenseFeatureGroupContentItem[] = []
  for (const item of items) {
    const categories = categoriesOf(item)
    if (categories.length === 0) unfiled.push(item)
    for (const code of categories) {
      const group = byCategory.get(code)
      if (group) group.push(item)
      else byCategory.set(code, [item])
    }
  }
  const groups: LicenseFeatureGroupContentGroup[] = [...byCategory.entries()]
    .map(([code, groupItems]) => ({
      categoryCode: code,
      label: categoryLabels.get(code) ?? code,
      items: groupItems,
    }))
    .sort(byLabel)
  if (unfiled.length > 0) {
    groups.push({
      categoryCode: null,
      label: NO_CATEGORY_LABEL,
      items: unfiled,
    })
  }
  return groups
}

function groupByCountries(
  items: readonly LicenseFeatureGroupContentItem[],
): LicenseFeatureGroupContentGroup[] {
  const byCountries = new Map<string, LicenseFeatureGroupContentItem[]>()
  for (const item of items) {
    const key = item.CountryCodes ?? NO_COUNTRY_LABEL
    const group = byCountries.get(key)
    if (group) group.push(item)
    else byCountries.set(key, [item])
  }
  return [...byCountries.entries()]
    .map(([label, groupItems]) => ({
      categoryCode: null,
      label,
      items: groupItems,
    }))
    .sort(byLabel)
}

/**
 * The content classes, in the order the rule options list them — only the
 * classes the app enforces (backoffice.EntitledFeatureTypes) arrive, which
 * is the whole point: a class here is one a package changes for a user.
 */
export function contentClassesOf(
  items: readonly LicenseFeatureGroupContentItem[],
  options: readonly LicenseFeatureGroupRuleOption[],
): LicenseFeatureGroupContentClass[] {
  const categoryLabels = new Map(
    options
      .filter(isCategoryRule)
      .map((option) => [option.LayerCategoryFeatureCode ?? "", option.Label]),
  )
  return options
    .filter(
      (option) => !isCategoryRule(option) && option.FeatureTypeCode != null,
    )
    .map((option) => {
      const featureTypeCode = option.FeatureTypeCode ?? -1
      const classItems = items.filter(
        (item) => item.FeatureTypeCode === featureTypeCode,
      )
      return {
        featureTypeCode,
        name: option.Label,
        description:
          classItems[0]?.FeatureTypeDescription ?? option.Description ?? null,
        items: classItems,
        groups: canGrantByCategory(featureTypeCode)
          ? groupLayersByCategory(classItems, categoryLabels)
          : groupByCountries(classItems),
      }
    })
}

/**
 * What the package does for a user in one class, in numbers: how many
 * items there are, how many of them packages even decide (the locked ones),
 * and how many of each this package grants.
 */
export type LicenseFeatureGroupContentImpact = {
  total: number
  granted: number
  locked: number
  grantedLocked: number
}

export function impactOf(
  items: readonly LicenseFeatureGroupContentItem[],
  isGranted: (item: LicenseFeatureGroupContentItem) => boolean,
): LicenseFeatureGroupContentImpact {
  const impact = { total: 0, granted: 0, locked: 0, grantedLocked: 0 }
  for (const item of items) {
    impact.total += 1
    const granted = isGranted(item)
    if (granted) impact.granted += 1
    if (isLockedContent(item)) {
      impact.locked += 1
      if (granted) impact.grantedLocked += 1
    }
  }
  return impact
}

/**
 * "Everyone gets all 38 base maps — none is locked, so no package decides."
 * Through the customer lens the subject is the customer's users and a
 * closed class (every item locked for them) reads as the exact list they
 * get: what is ticked here plus what their packages carry, nothing else.
 */
export function describeImpact(
  impact: LicenseFeatureGroupContentImpact,
  noun: string,
  lens: FeatureGroupLens = { kind: "package" },
) {
  const subject = subjectOfLens(lens)
  // "Lina gets", "House Stark's users get".
  const gets = isPersonLens(lens) ? "gets" : "get"
  const addedHere = isPersonLens(lens)
    ? "added on the customer panel"
    : "added here"
  if (impact.locked === 0) {
    return lens.kind === "customer"
      ? `${subject} ${gets} all ${pluralize(impact.total, noun)}: the class is open to them, every ${noun} is free for everyone, and new ones arrive on their own.`
      : `Everyone gets all ${pluralize(impact.total, noun)} — none is package-only, so no package decides.`
  }
  if (lens.kind === "customer" && impact.locked === impact.total) {
    return impact.granted === 0
      ? `${subject} ${gets} no ${noun}s until their licenses include some or some are ${addedHere}.`
      : `${subject} ${gets} exactly ${pluralize(impact.granted, noun)}: what their licenses include and what is ${addedHere}, nothing else.`
  }
  const everyone = impact.total - impact.locked
  if (lens.kind === "customer") {
    const missing = impact.locked - impact.grantedLocked
    const packageOnly =
      impact.locked === 1
        ? "the one package-only"
        : `all ${impact.locked} package-only ones`
    return missing === 0
      ? `${subject} ${gets} all ${pluralize(impact.total, noun)}: the ${everyone} free for everyone and ${packageOnly} through their licenses or the customer's own list.`
      : `${subject} ${gets} ${pluralize(impact.granted, noun)}: the ${everyone} free for everyone plus ${impact.grantedLocked} of the ${impact.locked} package-only ones; ${missing} need${missing === 1 ? "s" : ""} a package.`
  }
  const unlocks =
    impact.grantedLocked === impact.locked
      ? `all ${impact.locked} package-only`
      : `${impact.grantedLocked} of the ${impact.locked} package-only`
  return `${subject} get ${unlocks} ${noun}s${everyone > 0 ? `, plus the ${everyone} everyone gets` : ""}.`
}

/** Whether the item matches a filter typed into the class card. */
export function matchesContentFilter(
  item: LicenseFeatureGroupContentItem,
  filter: string,
  groupLabel: string,
) {
  const needle = filter.trim().toLowerCase()
  if (!needle) return true
  return (
    item.Name.toLowerCase().includes(needle) ||
    item.Code.toLowerCase().includes(needle) ||
    groupLabel.toLowerCase().includes(needle) ||
    countriesOf(item).some((code) => code.toLowerCase() === needle)
  )
}

/** "Base maps" → "base map": the class as the rows call one of its items. */
export function nounOf(contentClass: LicenseFeatureGroupContentClass) {
  return contentClass.name.toLowerCase().replace(/s$/, "")
}

/** A content item as the composition's picked set carries it. */
export function toPickedContent(
  item: LicenseFeatureGroupContentItem,
): FeatureGroupPickedLike {
  return {
    Code: item.Code,
    Name: item.Name,
    FeatureTypeCode: item.FeatureTypeCode,
    FeatureTypeName: item.FeatureTypeName,
    IsSurface: false,
  }
}
